import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { passwordReset } from '../core/_requests';
import { showError, showSuccess } from '../../../../_mel/customPartials/helper/Alerts';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import { useIntl } from 'react-intl';

const passwordResetSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, 'Minimum 8 Karakter / Minimum 8 symbols')
    .max(50, 'Maximum 50 karakter / Maximum 50 symbols')
    .required('Şifre gereklidir / Password is required'),
    
  changepassword: Yup.string()
    .min(8, 'Minimum 8 Karakter / Minimum 8 symbols')
    .max(50, 'Maximum 50 karakter / Maximum 50 symbols')
    .required('Şifre onayı gereklidir / Password confirmation is required')
    .oneOf([Yup.ref('password')], "Şifre ve Onay Şifresi eşleşmiyor / Password and Confirm Password didn't match"),
});

const initialValues = {
  password: '',
  changepassword: '',
};

export function ResetPassword() {
  const navigate = useNavigate();
  const intl = useIntl()
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  const code = searchParams.get('code') ?? '';
  const mail = searchParams.get('mail') ?? '';
  const mode = searchParams.get('m') ?? '';

  useEffect(() => { 
    if (!code) {
      showError('No code provided in the URL.');
      return;
    }
  }, [code]);

  const formik = useFormik({
    initialValues,
    validationSchema: passwordResetSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      try {
        const res = await passwordReset(mail, code, values.password);
        if (res.data?.result?.canLogin) {
          showSuccess("Şifre başarıyla değiştirildi / Password changed successfully!");
          setTimeout(() => {
            navigate('/auth');
          }, 1000);
        } else {
          setLoading(false);
          let message = res.data?.result?.message ?? "Bilinmeyen bir hata oluştu / Unknown error occurred!";
          showError(message);
        }
      } catch (error: any) {
        setLoading(false);
        let message = error?.response?.data?.error?.message ?? "Bilinmeyen bir hata oluştu / Unknown error occurred!";
        showError(message);
        setStatus('Girilen Bilgiler Hatalı / Details are incorrect');
        setSubmitting(false);
      }
    },
  });

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_signup_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>Şifre {mode === 'c' ? 'Oluştur' : 'Sıfırla'} <span className='text-muted ps-1'>/ {mode === 'c' ? 'Create Password' : 'Reset Password'}</span></h1>
      </div>
      {formik.status && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )}
      
      <div className='fv-row mb-8' data-kt-password-meter='true'>
        <div className='mb-1'>
          <label className='form-label fw-bolder text-dark fs-6'>Şifre <span className='text-muted ps-1'>/ Password</span></label>
          <div className='position-relative mb-3'>
            <input
              type='password'
              placeholder='Şifrenizi giriniz'
              autoComplete='off'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control bg-transparent',
                {
                  'is-invalid': formik.touched.password && formik.errors.password,
                },
                {
                  'is-valid': formik.touched.password && !formik.errors.password,
                }
              )}
            />
            {formik.touched.password && formik.errors.password && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.password}</span>
                </div>
              </div>
            )}
          </div>
          <div
            className='d-flex align-items-center mb-3'
            data-kt-password-meter-control='highlight'
          >
            <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
            <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
            <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
            <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px'></div>
          </div>
        </div>
        <div className='text-muted'>
          <b>En az 8 karakter kullanın ve harfler, sayılar ve semboller içermelidir.</b> <br />
          Use 8 or more characters with a mix of letters, numbers & symbols.
        </div>
      </div>
      
      <div className='fv-row mb-5'>
        <label className='form-label fw-bolder text-dark fs-6'>Şifre Tekrarı <span className='text-muted ps-1'>/ Confirm Password</span></label>
        <input
          type='password'
          placeholder='Şifrenizi tekrar giriniz'
          autoComplete='off'
          {...formik.getFieldProps('changepassword')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.changepassword && formik.errors.changepassword,
            },
            {
              'is-valid': formik.touched.changepassword && !formik.errors.changepassword,
            }
          )}
        />
        {formik.touched.changepassword && formik.errors.changepassword && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.changepassword}</span>
            </div>
          </div>
        )}
      </div>

      <div className='text-center'>
        <button type='submit' className='btn btn-lg btn-primary w-100 mb-5' disabled={loading}>
          {!loading && <span className='indicator-label'>{mode === 'c' ? 'Şifre Oluştur' : 'Şifreyi Sıfırlayın'} <span className='text-muted ps-1'>/ Submit</span></span>}
          {loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              Lütfen bekleyin... <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
    </form>
  );
}
